/**
 * HPKE — RFC 9180 base mode, single-shot (F4, ADR-12).
 *
 * Suite: kem_id=0x0010 (DHKEM-P256, HKDF-SHA256), kdf_id=0x0001 (HKDF-SHA256),
 * aead_id=0x0002 (AES-256-GCM). The AEAD is parameterized over Nk ∈ {16, 32} so
 * the official RFC test vectors (AES-128-GCM for this KEM) exercise the same
 * code path. Public keys serialize uncompressed (65 bytes) inside the KEM
 * context and `enc`. Labels exactly per RFC 9180 §4 / §5.1; seal at seq 0
 * (nonce = base_nonce).
 */
import { b64urlDecode, concatBytes, utf8 } from "./bytes.js";
import type { Jwk } from "./didkey.js";
import type { CryptoKey } from "./sign.js";

const KEM_ID = 0x0010;
const KDF_ID = 0x0001;
export const AEAD_AES256GCM = 0x0002;
export const AEAD_AES128GCM = 0x0001;

// ---------------------------------------------------------------------------
// HKDF-SHA256 (RFC 5869) via WebCrypto HMAC
// ---------------------------------------------------------------------------

async function hmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey(
    "raw",
    key.length ? key : new Uint8Array(32),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return new Uint8Array(await crypto.subtle.sign("HMAC", k, data));
}

const extract = (salt: Uint8Array, ikm: Uint8Array): Promise<Uint8Array> => hmac(salt, ikm);

async function expand(prk: Uint8Array, info: Uint8Array, len: number): Promise<Uint8Array> {
  if (len > 255 * 32) throw new Error("hkdf: expand length too large");
  let t: Uint8Array = new Uint8Array(0);
  const blocks: Uint8Array[] = [];
  for (let i = 1; blocks.reduce((n, b) => n + b.length, 0) < len; i++) {
    t = await hmac(prk, concatBytes(t, info, new Uint8Array([i])));
    blocks.push(t);
  }
  return concatBytes(...blocks).slice(0, len);
}

// ---------------------------------------------------------------------------
// Labeled extract/expand (RFC 9180 §4)
// ---------------------------------------------------------------------------

const I2OSP2 = (n: number): Uint8Array => {
  if (n < 0 || n > 0xffff) throw new Error("I2OSP2: out of range");
  return new Uint8Array([n >> 8, n & 0xff]);
};
const kemSuiteId = concatBytes(utf8("KEM"), I2OSP2(KEM_ID));
const hpkeSuiteId = (aeadId: number): Uint8Array =>
  concatBytes(utf8("HPKE"), I2OSP2(KEM_ID), I2OSP2(KDF_ID), I2OSP2(aeadId));

const labeledExtract = (
  suiteId: Uint8Array,
  salt: Uint8Array,
  label: string,
  ikm: Uint8Array,
): Promise<Uint8Array> => extract(salt, concatBytes(utf8("HPKE-v1"), suiteId, utf8(label), ikm));

const labeledExpand = (
  suiteId: Uint8Array,
  prk: Uint8Array,
  label: string,
  info: Uint8Array,
  len: number,
): Promise<Uint8Array> =>
  expand(prk, concatBytes(I2OSP2(len), utf8("HPKE-v1"), suiteId, utf8(label), info), len);

// ---------------------------------------------------------------------------
// DHKEM(P-256, HKDF-SHA256) — ECDH via WebCrypto deriveBits
// ---------------------------------------------------------------------------

async function importEcdhPublicKey(point65: Uint8Array): Promise<CryptoKey> {
  if (point65.length !== 65 || point65[0] !== 0x04) {
    throw new Error("hpke: public key must be a 65-byte uncompressed P-256 point");
  }
  return crypto.subtle.importKey("raw", point65, { name: "ECDH", namedCurve: "P-256" }, false, []);
}

/** Import a private ECDH JWK (d/x/y) for HPKE decap / need-to-know opening. */
export async function importEcdhPrivateKey(jwk: Jwk): Promise<CryptoKey> {
  const { key_ops: _ops, ext: _ext, ...cleanJwk } = jwk;
  return crypto.subtle.importKey("jwk", cleanJwk, { name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveBits",
  ]);
}

/**
 * Generate a P-256 encryption (ECDH) keypair. Extractable for the same reason
 * as `generateSigningKeypair` — vector generation and server-side tooling.
 */
export async function generateEncryptionKeypair(): Promise<{
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicJwk: Jwk;
  publicPoint: Uint8Array;
}> {
  const kp = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveBits",
  ]);
  const publicJwk = (await crypto.subtle.exportKey("jwk", kp.publicKey)) as Jwk;
  const publicPoint = new Uint8Array(await crypto.subtle.exportKey("raw", kp.publicKey));
  return { privateKey: kp.privateKey, publicKey: kp.publicKey, publicJwk, publicPoint };
}

const ecdhX = async (privateKey: CryptoKey, publicKey: CryptoKey): Promise<Uint8Array> =>
  new Uint8Array(await crypto.subtle.deriveBits({ name: "ECDH", public: publicKey }, privateKey, 256));

async function extractAndExpand(dh: Uint8Array, kemContext: Uint8Array): Promise<Uint8Array> {
  const eae = await labeledExtract(kemSuiteId, new Uint8Array(0), "eae_prk", dh);
  return labeledExpand(kemSuiteId, eae, "shared_secret", kemContext, 32);
}

/** 65-byte uncompressed point from a public JWK (x/y). */
function pointFromJwk(jwk: Jwk): Uint8Array {
  if (!jwk.x || !jwk.y) throw new Error("hpke: JWK missing x/y");
  return concatBytes(new Uint8Array([0x04]), b64urlDecode(jwk.x), b64urlDecode(jwk.y));
}

/**
 * DHKEM encap. `testSkE` is test-only: fixed ephemeral keys destroy security
 * if used in production paths — only vector generation/verification may pass it.
 */
export async function dhkemEncap(
  pkR65: Uint8Array,
  testSkE?: Jwk,
): Promise<{ sharedSecret: Uint8Array; enc: Uint8Array }> {
  const pkR = await importEcdhPublicKey(pkR65);
  let skE: CryptoKey;
  let enc: Uint8Array;
  if (testSkE) {
    skE = await importEcdhPrivateKey(testSkE);
    enc = pointFromJwk(testSkE);
  } else {
    const kp = await generateEncryptionKeypair();
    skE = kp.privateKey;
    enc = kp.publicPoint;
  }
  const dh = await ecdhX(skE, pkR);
  const kemContext = concatBytes(enc, pkR65);
  return { sharedSecret: await extractAndExpand(dh, kemContext), enc };
}

export async function dhkemDecap(
  enc65: Uint8Array,
  skR: CryptoKey,
  pkR65: Uint8Array,
): Promise<Uint8Array> {
  const pkE = await importEcdhPublicKey(enc65);
  const dh = await ecdhX(skR, pkE);
  const kemContext = concatBytes(enc65, pkR65);
  return extractAndExpand(dh, kemContext);
}

// ---------------------------------------------------------------------------
// Key schedule (RFC 9180 §5.1, mode_base) + single-shot seal/open
// ---------------------------------------------------------------------------

function nkOf(aeadId: number): number {
  if (aeadId === AEAD_AES256GCM) return 32;
  if (aeadId === AEAD_AES128GCM) return 16;
  throw new Error(`hpke: unsupported aead_id ${aeadId}`);
}

export async function keySchedule(
  aeadId: number,
  sharedSecret: Uint8Array,
  info: Uint8Array,
): Promise<{ key: Uint8Array; baseNonce: Uint8Array }> {
  const sid = hpkeSuiteId(aeadId);
  const pskIdHash = await labeledExtract(sid, new Uint8Array(0), "psk_id_hash", new Uint8Array(0));
  const infoHash = await labeledExtract(sid, new Uint8Array(0), "info_hash", info);
  const ksc = concatBytes(new Uint8Array([0x00]), pskIdHash, infoHash); // mode_base
  const secret = await labeledExtract(sid, sharedSecret, "secret", new Uint8Array(0));
  return {
    key: await labeledExpand(sid, secret, "key", ksc, nkOf(aeadId)),
    baseNonce: await labeledExpand(sid, secret, "base_nonce", ksc, 12),
  };
}

async function importAeadKey(key: Uint8Array, usage: "encrypt" | "decrypt"): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", key, { name: "AES-GCM" }, false, [usage]);
}

/**
 * Single-shot HPKE seal at seq 0 (nonce = base_nonce). WebCrypto output is ct||tag, matching RFC `ct`.
 * `opts.testSkE` is test-only: fixed ephemeral keys destroy security if used
 * in production paths — only vector generation/verification may pass it.
 */
export async function hpkeSeal(
  pkR65: Uint8Array,
  info: Uint8Array,
  aad: Uint8Array,
  pt: Uint8Array,
  opts?: { aeadId?: number; testSkE?: Jwk },
): Promise<{ enc: Uint8Array; ct: Uint8Array }> {
  const aeadId = opts?.aeadId ?? AEAD_AES256GCM;
  const { sharedSecret, enc } = await dhkemEncap(pkR65, opts?.testSkE);
  const { key, baseNonce } = await keySchedule(aeadId, sharedSecret, info);
  const k = await importAeadKey(key, "encrypt");
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: baseNonce, additionalData: aad }, k, pt),
  );
  return { enc, ct };
}

/** Single-shot HPKE open; throws on tag failure. */
export async function hpkeOpen(
  skR: CryptoKey,
  pkR65: Uint8Array,
  enc: Uint8Array,
  info: Uint8Array,
  aad: Uint8Array,
  ct: Uint8Array,
  opts?: { aeadId?: number },
): Promise<Uint8Array> {
  const aeadId = opts?.aeadId ?? AEAD_AES256GCM;
  const sharedSecret = await dhkemDecap(enc, skR, pkR65);
  const { key, baseNonce } = await keySchedule(aeadId, sharedSecret, info);
  const k = await importAeadKey(key, "decrypt");
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt({ name: "AES-GCM", iv: baseNonce, additionalData: aad }, k, ct),
    );
  } catch {
    throw new Error("hpke: open failed (AEAD tag mismatch)");
  }
}
